'use client'

import { setDefaultOptions } from 'date-fns'
import { enUS, ja } from 'date-fns/locale'
import Image from 'next/image'
import { useRouter } from 'next/navigation'
import { useTranslation } from 'react-i18next'

import { DEFAULT_LOCALE, JAPAN_LOCALE } from '~/core/constants/env'
import ComboboxSelect from '~/core/ui/ComboboxSelect'
import type { ISelectOption } from '~/core/ui/Select'
import { TypographyText } from '~/core/ui/Text'

import { useClient } from '~/lib/hooks/use-is-client'
import { useLanguageSwitcher } from '~/lib/i18n/client'
import { useRouterContext } from '~/lib/next/use-router-context'

export const LANG_ENG_OPTION = {
  value: DEFAULT_LOCALE,
  flag: (
    <Image src={'/img/icon-languages/flag-us.svg'} width={24} height={16} alt={'flag en'} />
  ) as any,
  supportingObj: {
    name: 'English'
  }
}
export const LANG_JA_OPTION = {
  value: JAPAN_LOCALE,
  flag: (
    <Image src={'/img/icon-languages/flag-ja.svg'} width={24} height={16} alt={'flag ja'} />
  ) as any,
  supportingObj: {
    name: '日本語'
  }
}

const LANGUAGES_OPTIONS: ISelectOption[] = [LANG_ENG_OPTION, LANG_JA_OPTION]

const DropdownLanguage = ({
  languagesOptions = LANGUAGES_OPTIONS,
  preventAction = false
}: {
  languagesOptions?: ISelectOption[]
  preventAction?: boolean
}) => {
  const router = useRouter()
  const { asPath } = useRouterContext()
  const { i18n } = useTranslation()
  const { changeLanguage } = useLanguageSwitcher()
  const { isClient } = useClient()

  return isClient ? (
    <ComboboxSelect
      type="unstyled"
      configSelectOption={{
        flag: true,
        supportingText: ['name']
      }}
      onChange={(value) => {
        if (!preventAction) {
          setDefaultOptions({
            locale: (value as ISelectOption).value === JAPAN_LOCALE ? ja : enUS
          })
          changeLanguage((value as ISelectOption).value as string)
        }
      }}
      size="sm"
      value={languagesOptions.find((language) => language.value === i18n.language)}
      getCustomValueRender={(value) => (
        <div className="flex cursor-pointer items-center justify-center">
          {(value as ISelectOption)?.flag || ''}

          <div className="mx-2">
            <TypographyText className="text-center text-sm text-gray-900 dark:text-gray-300">
              {(value as ISelectOption)?.supportingObj?.name || ''}
            </TypographyText>
          </div>
        </div>
      )}
      isSearchable={false}
      menuOptionSide="bottom"
      closeOnSelect
      isClearable={false}
      menuOptionAlign="start"
      dropdownMenuClassName="min-w-[148px] max-w-[148px]"
      buttonClassName="max-w-[248px]"
      options={languagesOptions}
    />
  ) : null
}

export default DropdownLanguage
